import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ManagerSidebar from '../../../components/Sidebar/ManagerSidebar';
import './TenantEdit.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faCalendar, faHome, faMoneyBill,
    faEnvelope, faGlobe, faPassport, faPhone,
    faUsers, faChild, faFileUpload, faHandHoldingUsd,
    faMapMarkerAlt, faExternalLinkAlt, faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';

const EditTenant = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [tenantData, setTenantData] = useState({
        guest_name: '',
        arrival_date: '',
        departure_date: '',
        unitname: '',
        rent_usd: 0,
        rent_rwf: 0,
        deposites: 0,
        email_address: '',
        nationalite: '',
        country_residence: '',
        passport_id: '',
        number_adults: 1,
        number_child: 0,
        mobile_number: '',
        price_per_month: 0,
        number_of_months: '',
        duration_type: 'days'
    });

    // Currency state matching TenantAdd structure
    const [currency, setCurrency] = useState('RWF');
    const [exchangeRate, setExchangeRate] = useState('');

    const [allApartmentUnits, setAllApartmentUnits] = useState([]);
    const [originalUnitName, setOriginalUnitName] = useState('');
    const [passportFile, setPassportFile] = useState(null);
    const [existingPassportUrl, setExistingPassportUrl] = useState('');

    // =========================
    // LOAD DATA
    // =========================
    useEffect(() => {
        const load = async () => {
            await fetchAllApartmentUnits();
            await fetchTenant();
        };
        load();
    }, [id]);

    // =========================
    // FORMAT DATE
    // =========================
    const formatDateLocal = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-CA'); // YYYY-MM-DD
    };

    // =========================
    // DURATION CALCULATION
    // =========================
    const calculateDuration = (start, end) => {
        if (!start || !end) return { months: 0, extraDays: 0, totalDays: 0 };

        const startDate = new Date(start);
        const endDate = new Date(end);

        if (endDate <= startDate) return { months: 0, extraDays: 0, totalDays: 0 };

        const timeDiff = endDate.getTime() - startDate.getTime();
        const totalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

        let months =
            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
            (endDate.getMonth() - startDate.getMonth());

        if (endDate.getDate() < startDate.getDate()) months--;
        if (months < 0) months = 0;

        const tempDate = new Date(startDate);
        tempDate.setMonth(tempDate.getMonth() + months);

        const extraTimeDiff = endDate.getTime() - tempDate.getTime();
        const extraDays = Math.ceil(extraTimeDiff / (1000 * 60 * 60 * 24));

        return { months, extraDays, totalDays };
    };

    // =========================
    // COMPUTE RENT VALUES
    // =========================
    const computeRentValues = (updatedData, selectedCurrency, rate) => {
        const duration = calculateDuration(
            updatedData.arrival_date,
            updatedData.departure_date
        );

        const monthlyPrice = updatedData.price_per_month || 0;

        const year = new Date(updatedData.arrival_date).getFullYear();
        const month = new Date(updatedData.arrival_date).getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const dailyRate = monthlyPrice / daysInMonth;

        let rawRent = 0;

        if (duration.months >= 1) {
            updatedData.duration_type = 'months';
            updatedData.number_of_months =
                `${duration.months} Month(s) ${duration.extraDays} Day(s)`;
            rawRent = Math.round(
                duration.months * monthlyPrice + duration.extraDays * dailyRate
            );
        } else {
            updatedData.duration_type = 'days';
            updatedData.number_of_months = `${duration.totalDays} Day(s)`;
            rawRent = duration.totalDays * monthlyPrice;
        }

        if (selectedCurrency === 'RWF') {
            updatedData.rent_rwf = rawRent;
            updatedData.rent_usd = 0;
        } else {
            const parsedRate = parseFloat(rate) || 0;
            updatedData.rent_usd = rawRent;
            updatedData.rent_rwf = parsedRate > 0
                ? Math.round(rawRent * parsedRate)
                : 0;
        }

        return updatedData;
    };

    // =========================
    // FETCH TENANT
    // =========================
    const fetchTenant = async () => {
        try {
            const res = await axios.get(
                'https://ikaze-thouse-backend.vercel.app/api/v1/tenants/getAll'
            );

            const tenants = res.data.data || [];
            const tenant = tenants.find(t => t.tenant_id === parseInt(id));

            if (!tenant) return;

            const duration = calculateDuration(
                tenant.arrival_date,
                tenant.departure_date
            );

            // Parse saved currency or infer it from the dataset structures
            const activeCurrency = tenant.currency || (Number(tenant.rent_usd) > 0 ? 'USD' : 'RWF');
            const activeExchangeRate = tenant.exchange_rate || tenant.exchangeRate || '';

            setCurrency(activeCurrency);
            setExchangeRate(activeExchangeRate);

            setTenantData({
                ...tenant,
                arrival_date: formatDateLocal(tenant.arrival_date),
                departure_date: formatDateLocal(tenant.departure_date),
                unitname: tenant.appartment_unite || tenant.unitname || '',
                deposites: Number(tenant.deposites) || 0,
                price_per_month: Number(tenant.price_per_month) || 0,
                rent_usd: Number(tenant.rent_usd) || 0,
                rent_rwf: Number(tenant.rent_rwf) || 0,
                duration_type: tenant.duration_type || (duration.months >= 1 ? 'months' : 'days'),
                number_of_months: duration.months >= 1 
                    ? `${duration.months} Month(s) ${duration.extraDays} Day(s)`
                    : `${duration.totalDays} Day(s)`
            });

            setOriginalUnitName(tenant.appartment_unite || tenant.unitname || '');
            setExistingPassportUrl(tenant.passport_file || tenant.passport || '');

        } catch (err) {
            console.error(err);
        }
    };

    // =========================
    // FETCH UNITS
    // =========================
    const fetchAllApartmentUnits = async () => {
        try {
            const res = await axios.get(
                'https://ikaze-thouse-backend.vercel.app/api/v1/appartmentUnits/getAll'
            );

            if (res.data.success) {
                setAllApartmentUnits(res.data.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // =========================
    // HANDLE INPUT CHANGE
    // =========================
    const handleChange = (e) => {
        const { name, value } = e.target;

        let updatedData = {
            ...tenantData,
            [name]:
                name.includes('number') ||
                name.includes('price') ||
                name === 'deposites'
                    ? Number(value)
                    : value
        };

        updatedData = computeRentValues(updatedData, currency, exchangeRate);
        setTenantData(updatedData);
    };

    // =========================
    // HANDLE CURRENCY CHANGE
    // =========================
    const handleCurrencyChange = (e) => {
        const selected = e.target.value;
        setCurrency(selected);

        const rate = selected === 'RWF' ? '' : exchangeRate;
        setExchangeRate(rate);

        const updatedData = computeRentValues({ ...tenantData }, selected, rate);
        setTenantData(updatedData);
    };

    // =========================
    // HANDLE EXCHANGE RATE CHANGE
    // =========================
    const handleExchangeRateChange = (e) => {
        const rate = e.target.value;
        setExchangeRate(rate);

        const updatedData = computeRentValues({ ...tenantData }, currency, rate);
        setTenantData(updatedData);
    };

    // =========================
    // FILE CHANGE
    // =========================
    const handleFileChange = (e) => {
        setPassportFile(e.target.files[0]);
    };

    // =========================
    // SAVE UPDATE
    // =========================
    const handleSave = async () => {
        if (!tenantData.unitname) return alert('Select unit');
        if (!tenantData.arrival_date || !tenantData.departure_date) return alert('Select dates');
        if (currency === 'USD' && (!exchangeRate || parseFloat(exchangeRate) <= 0)) {
            return alert('Enter a valid exchange rate for USD');
        }

        const formData = new FormData();

        Object.keys(tenantData).forEach(key => {
            formData.append(
                key === 'unitname' ? 'appartment_unite' : key,
                tenantData[key]
            );
        });

        formData.append('currency', currency);
        formData.append('exchange_rate', currency === 'USD' ? exchangeRate : '');

        if (passportFile) {
            formData.append('passport_file', passportFile);
        }

        try {
            await axios.put(
                `https://ikaze-thouse-backend.vercel.app/api/v1/tenants/update/${id}`,
                formData
            );

            // Handle unit tracking logic variations across updates
            if (originalUnitName && originalUnitName !== tenantData.unitname) {
                await axios.put(
                    `https://ikaze-thouse-backend.vercel.app/api/v1/appartmentUnits/makeAvailable/${encodeURIComponent(originalUnitName)}`
                );

                await axios.put(
                    `https://ikaze-thouse-backend.vercel.app/api/v1/appartmentUnits/occupy/${encodeURIComponent(tenantData.unitname)}`
                );
            }

            alert('Tenant updated successfully!');
            navigate('/manager/tenants');

        } catch (err) {
            console.error(err);
            alert('Failed to update tenant');
        }
    };

    // =========================
    // DISPLAY RENT SUMMARY
    // =========================
    const rentDisplay = currency === 'RWF'
        ? `RWF ${tenantData.rent_rwf.toLocaleString()}`
        : `$ ${tenantData.rent_usd.toLocaleString()} USD  →  RWF ${tenantData.rent_rwf.toLocaleString()}`;

    return (
        <div className="add-container">
            <ManagerSidebar />

            <div className="add-tenant-form">

                {/* HEADER */}
                <div className="form-header">
                    <h2>🏠 Edit Tenant Details</h2>
                    <p>Modify the lease metrics and personal fields data maps below.</p>
                </div>

                <div className="form-grid">

                    {/* LEASE SECTION */}
                    <h3 className="section-title">Lease & Unit Details</h3>

                    {/* UNIT */}
                    <div className="input-group">
                        <label className="field-label">Apartment Unit</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faHome} className="input-icon" />
                            <select name="unitname" value={tenantData.unitname} onChange={handleChange}>
                                <option value="">Select Unit</option>
                                {allApartmentUnits
                                    .filter(u => u.status === 'Available' || u.unitname === originalUnitName)
                                    .map(unit => (
                                        <option key={unit.UnitId} value={unit.unitname}>
                                            {unit.unitname}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>

                    {/* ARRIVAL DATE */}
                    <div className="input-group">
                        <label className="field-label">Arrival Date</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                            <input type="date" name="arrival_date" value={tenantData.arrival_date} onChange={handleChange} />
                        </div>
                    </div>

                    {/* DEPARTURE DATE */}
                    <div className="input-group">
                        <label className="field-label">Departure Date</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                            <input type="date" name="departure_date" value={tenantData.departure_date} onChange={handleChange} />
                        </div>
                    </div>

                    {/* CURRENCY SELECT */}
                    <div className="input-group">
                        <label className="field-label">Payment Currency</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faMoneyBill} className="input-icon" />
                            <select value={currency} onChange={handleCurrencyChange}>
                                <option value="RWF">RWF (Rwandan Franc)</option>
                                <option value="USD">USD (US Dollar)</option>
                            </select>
                        </div>
                    </div>

                    {/* EXCHANGE RATE — shown if USD */}
                    {currency === 'USD' && (
                        <div className="input-group">
                            <label className="field-label">Exchange Rate (1 USD = ? RWF)</label>
                            <div className="input-wrapper">
                                <FontAwesomeIcon icon={faExchangeAlt} className="input-icon" />
                                <input
                                    type="number"
                                    placeholder="e.g. 1300"
                                    value={exchangeRate}
                                    onChange={handleExchangeRateChange}
                                    min="1"
                                />
                            </div>
                        </div>
                    )}

                    {/* PRICE */}
                    <div className="input-group">
                        <label className="field-label">
                            Price per Month / Day ({currency})
                        </label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faHandHoldingUsd} className="input-icon" />
                            <input
                                type="number"
                                name="price_per_month"
                                value={tenantData.price_per_month || ''}
                                placeholder={`Enter price in ${currency}`}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* DURATION */}
                    <div className="input-group">
                        <label className="field-label">Duration (Auto)</label>
                        <div className="input-wrapper no-icon">
                            <input
                                value={tenantData.number_of_months}
                                readOnly
                                className="readonly-field"
                            />
                        </div>
                    </div>

                    {/* TOTAL RENT */}
                    <div className="input-group">
                        <label className="field-label">Total Rent (Auto)</label>
                        <div className="input-wrapper no-icon">
                            <input
                                value={rentDisplay}
                                readOnly
                                className="readonly-field highlight-text"
                            />
                        </div>
                    </div>

                    {/* PERSONAL INFORMATION */}
                    <h3 className="section-title">Tenant Personal Information</h3>

                    {/* FULL NAME */}
                    <div className="input-group">
                        <label className="field-label">Full Name</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faUser} className="input-icon" />
                            <input
                                type="text"
                                name="guest_name"
                                value={tenantData.guest_name}
                                placeholder="John Doe"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* EMAIL */}
                    <div className="input-group">
                        <label className="field-label">Email Address</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                            <input
                                type="email"
                                name="email_address"
                                value={tenantData.email_address}
                                placeholder="mail@example.com"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* PHONE */}
                    <div className="input-group">
                        <label className="field-label">Mobile Number</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faPhone} className="input-icon" />
                            <input
                                type="text"
                                name="mobile_number"
                                value={tenantData.mobile_number}
                                placeholder="+250..."
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* PASSPORT ID */}
                    <div className="input-group">
                        <label className="field-label">Passport / ID</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faPassport} className="input-icon" />
                            <input
                                type="text"
                                name="passport_id"
                                value={tenantData.passport_id}
                                placeholder="ID Number"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* NATIONALITY */}
                    <div className="input-group">
                        <label className="field-label">Nationality</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faGlobe} className="input-icon" />
                            <input
                                type="text"
                                name="nationalite"
                                value={tenantData.nationalite}
                                placeholder="Nationality"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* COUNTRY */}
                    <div className="input-group">
                        <label className="field-label">Country</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                            <input
                                type="text"
                                name="country_residence"
                                value={tenantData.country_residence}
                                placeholder="Country"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* OCCUPANCY & SECURITY */}
                    <h3 className="section-title">Occupancy & Security</h3>

                    {/* DEPOSIT */}
                    <div className="input-group">
                        <label className="field-label">Security Deposit</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faMoneyBill} className="input-icon" />
                            <input
                                type="number"
                                name="deposites"
                                value={tenantData.deposites || ''}
                                placeholder="0.00"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* ADULTS */}
                    <div className="input-group">
                        <label className="field-label">Number of Adults</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faUsers} className="input-icon" />
                            <input
                                type="number"
                                name="number_adults"
                                value={tenantData.number_adults}
                                min="1"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* CHILDREN */}
                    <div className="input-group">
                        <label className="field-label">Number of Children</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faChild} className="input-icon" />
                            <input
                                type="number"
                                name="number_child"
                                value={tenantData.number_child}
                                min="0"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* FILE ATTACHMENT DISPLAY */}
                    <div className="input-group file-upload-section">
                        <label className="field-label">Passport File Document</label>
                        <div className="input-wrapper file-flex">
                            <FontAwesomeIcon icon={faFileUpload} className="input-icon" />
                            <input type="file" onChange={handleFileChange} />
                            {existingPassportUrl && (
                                <a 
                                    href={existingPassportUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="view-file-link"
                                >
                                    View Current <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                                </a>
                            )}
                        </div>
                    </div>

                </div>

                <div className="form-buttons">
                    <button type="button" onClick={() => navigate('/manager/tenants')}>Cancel</button>
                    <button type="button" onClick={handleSave}>Save Changes</button>
                </div>

            </div>
        </div>
    );
};

export default EditTenant;